// ABOUTME: Info tab content for ticket details panel in service desk.
// ABOUTME: Displays main ticket information like status, assignee, dates, etc.

import { Box, ButtonBase, Collapse, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Text from "../../../../../../components/theme/text/Text";
import Subheading from "../../../../../../components/theme/text/Subheading";
import { themeSettings, tokens } from "../../../../../../theme";
import ShLabel from "../../../../../../components/theme/label/ShLabel";
import { DropdownBaseV2, ShDropdownQuery } from "../../../../../../components/theme/dropdowns/Dropdown";
import { useGetUsersSelectQuery } from "../../../../../../newapi/user/userSlice";
import {
    useGetSupportTicketLabelsQuery,
    useAddSupportTicketLabelMutation,
    useRemoveSupportTicketLabelMutation,
    useRemoveSupportTicketLinkedItemMutation,
    useUpdateSupportTicketMutation,
} from "../../../../../../newapi/warehouse/supportTicketApiSlice";
import { search_keys } from "../../../../../../utils/searchKeys";
import { support_ticket_linked_type_to_search_key, support_ticket_status } from "../../../../../../utils/staticEnums";
import { getSupportTicketStateData } from "../../../../../../utils/labelColorText";
import MConfirmation from "../../../../../../components/global/Modals/MConfirmation";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../../components/theme/TabView3/IconColorGenerator";
import MAddSupportTicketLinkedItem from "../../../../../../components/global/Modals/MAddSupportTicketLinkedItem";
import MTicketDetailBottom from "../modals/MTicketDetailBottom";
import SidepanelLoader from "../../../../../../components/theme/sidepanel2/SidepanelLoader";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import {
    showToastMessagePostRequest,
    showToastMessageUpdateRequest,
    showToastMessageRequestError,
} from "../../../../../../utils/toasts";
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { getContrastText } from "../../../../../global/branding/brandConfig";
import { hexToRgb } from "../../../../../../utils/colorConverters";

import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';



const SpOrderDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));
const SpInvoiceDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpInvoiceDetails/SpInvoiceDetails"));
const SPStockDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails"));
const SpInboundDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpInboundDetails/SpInboundDetails"));
const SpReturnDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpReturnDetails/SpReturnDetails"));
const SpWebshopCredentialsDetails = SidepanelLoader(() => import("../../../../../../components/global/Sidepanels/SpWebshopCredentialsDetails/SpWebshopCredentialsDetails"));

const MESSAGE_TYPES = {
    FULFILMENTCENTER: 'fulfilmentcenter',
    STOCKHUB: 'stockhub',
    CLIENT: 'client',
};

const MESSAGE_TYPE_ORDER = [MESSAGE_TYPES.CLIENT, MESSAGE_TYPES.FULFILMENTCENTER, MESSAGE_TYPES.STOCKHUB];

// Assign-to targets: only servicedesk-capable users. The named filter maps to
// its privilege set server-side; admins always pass regardless of privileges.
const ASSIGNEE_FILTER = { filter: "servicedesk_assignee" };

const formatDate = (value, language) =>
    value ? new Date(value).toLocaleDateString(language) : "";

const DetailsTabInfo = (props) => {
    const { messages = [] } = props;
    // Either explicit lock (linked-ticket-from-details click) or the ticket
    // itself is CLOSED — both hide edit affordances on labels + linked items.
    const isLocked = props.locked || props.ticket?.status === support_ticket_status.CLOSED;

    return (
        <>
            <DetailsMainInfo ticket={props.ticket} locked={isLocked}/>
            <DetailsLinkedItems ticket={props.ticket} locked={isLocked}/>
            <DetailsParticipants messages={messages} ticket={props.ticket} locked={isLocked}/>
        </>
    );
};

export default DetailsTabInfo;


const DetailsMainInfo = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t, i18n } = useTranslation();

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === "RELATION";

    return (
        <Box
            sx={{
                mt:1
            }}
        >
             <Subheading semibold>{t("ticket_details")}</Subheading>
            <Box
                sx={{
                    mt:0.5,
                }}
            >
                <Box
                    sx={{
                        py:1.5,
                        display:'flex',
                        alignItems:"center",
                        justifyContent:'space-between',
                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <Text light>
                        {t("relation")}
                    </Text>
                    <Text bold>
                        {props.ticket?.relation?.name}
                    </Text>
                </Box>
                <Box
                    sx={{
                        py:1.5,
                        display:'flex',
                        alignItems:"center",
                        justifyContent:'space-between',
                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <Text light>
                        {t("created_by")}
                    </Text>
                    <Text>
                        {props.ticket?.creator?.name}
                    </Text>
                </Box>
                <Box
                    sx={{
                        py:1.5,
                        display:'flex',
                        alignItems:"center",
                        justifyContent:'space-between',
                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <Text light>
                        {t("created_at")}
                    </Text>
                    <Text >
                        {formatDate(props.ticket?.createdAt, i18n.language)}
                    </Text>
                </Box>
                <Box
                    sx={{
                        py:1.5,
                        display:'flex',
                        alignItems:"center",
                        justifyContent:'space-between',
                        borderBottom:`2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <Text light>
                        {t("updated_at")}
                    </Text>
                    <Text >
                        {formatDate(props.ticket?.updatedAt, i18n.language)}
                    </Text>
                </Box>
                <Box
                    sx={{
                        py:1.5,
                        pb:isRelation ? 0.5 : 1.5,
                        display:'flex',
                        alignItems:"center",
                        justifyContent:'space-between',
                        borderBottom:isRelation ? null : `2px solid ${theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100]}`,
                    }}
                >
                    <Text light>
                        {t("state")}
                    </Text>
                    <TicketStatusLabel status={props.ticket?.status} theme={theme} />
                </Box>
                {!isRelation && <DetailsLabelsRow ticket={props.ticket} locked={props.locked}/>}
            </Box>
        </Box>
    )
}

const LabelDropdownOption = (props) => {
    const option = props.option ?? {};
    return (
        <Box
            sx={{
                // height:24,
                display: "flex",
                alignItems: "center",
                gap: 1,
                // py: 1,
                width: 1,
                borderRadius:10,
            }}
        >
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 2,
                    background: option.color,
                    flexShrink: 0,
                }}
            />
            <Text semibold>{option.shortName}</Text>
            {option.longName && (
                <Text light noBreak sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {option.longName}
                </Text>
            )}
        </Box>
    );
};

const DetailsLabelsRow = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    // This row only renders on the fulfilment side, where labels are relevant —
    // show them expanded by default instead of hidden behind the chevron.
    const [expanded, setExpanded] = useState(true);

    const { data: allLabelsData } = useGetSupportTicketLabelsQuery();
    const [addLabel] = useAddSupportTicketLabelMutation();
    const [removeLabel] = useRemoveSupportTicketLabelMutation();

    const ticketLabels = props.ticket?.labels ?? [];
    const allLabels = allLabelsData?.data ?? [];

    const availableLabels = useMemo(() => {
        const assignedIds = new Set(ticketLabels.map((l) => l.id));
        return allLabels.filter((l) => !assignedIds.has(l.id));
    }, [ticketLabels, allLabels]);

    const handleAdd = async (selected) => {
        if (selected === -1 || !props.ticket?.id) return;
        const label = availableLabels[selected];
        if (!label) return;
        try {
            const result = await addLabel({
                id: props.ticket.id,
                data: { labelId: label.id },
            }).unwrap();
            showToastMessagePostRequest(t, result);
        } catch (err) {
            showToastMessageRequestError(t("create_failed"), t, err);
        }
    };

    const handleRemove = async (labelId) => {
        if (!props.ticket?.id) return;
        try {
            const result = await removeLabel({
                id: props.ticket.id,
                labelId,
            }).unwrap();
            showToastMessageUpdateRequest(t, result);
        } catch (err) {
            showToastMessageRequestError(t("delete_failed"), t, err);
        }
    };

    return (
        <Box
            sx={{
                py: 1.5,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <ButtonBase
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: 1,
                    borderRadius: 2,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Text light>{t("labels")}</Text>
                    {!props.locked && availableLabels.length > 0 && (
                        <DropdownBaseV2
                            displayName={"shortName"}
                            searchOptions={{
                                enabled: true,
                                placeholder: t("search"),
                                searchables: ["longName"],
                            }}
                            options={availableLabels}
                            selected={-1}
                            onChange={handleAdd}
                            shComponent={<LabelDropdownOption />}
                            defaultStyling
                            closeOnSelection
                            resetOnSelection
                            disallowDeselection
                            stopPropagation
                            styles={{
                                optionSx: {
                                    paddingX:1,
                                    pl:0.5,
                                    py:0,
                                    borderRadius:3,
                                    height:24,
                                }
                            }}
                        >
                            {({ onClick }) => (
                                <ButtonBase
                                    component="div"
                                    onClick={onClick}
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: 3,
                                        background:
                                            theme.palette.mode === "dark"
                                                ? colors.grey[400]
                                                : colors.grey[100],
                                        color: colors.txt["primary"],
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AddIcon sx={{ fontSize: 16 }} />
                                </ButtonBase>
                            )}
                        </DropdownBaseV2>
                    )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Text bold>{ticketLabels.length || 0}</Text>
                    <ChevronRightIcon
                        sx={{
                            fontSize: 17,
                            color: colors.txt["secondary"],
                            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                            transition: "transform 200ms",
                        }}
                    />
                </Box>
            </ButtonBase>
            <Collapse in={expanded} timeout={200}>
                <Box
                    sx={{
                        mt: 1,
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        alignItems: "center",
                    }}
                >
                    {ticketLabels.map((label) => {
                        const contrast = getContrastText(hexToRgb(label.color));
                        return (
                            <Box
                                key={"tl" + label.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    height: 24,
                                    px:1.5,
                                    pr:0.5,
                                    borderRadius: 3,
                                    background: label.color,
                                    color: contrast,
                                    fontFamily: "Roboto, sans-serif",
                                    fontSize: 13,
                                    fontWeight: 700,
                                }}
                            >
                                {label.longName}
                                {!props.locked && (
                                    <ButtonBase
                                        onClick={() => handleRemove(label.id)}
                                        sx={{
                                            width: 16,
                                            height: 16,
                                            borderRadius: "50%",
                                            color: contrast,
                                            fontWeight:400,
                                            "&:hover": {
                                                background:
                                                    contrast === "#fff"
                                                        ? "rgba(0,0,0,0.15)"
                                                        : "rgba(0,0,0,0.15)",
                                            },
                                        }}
                                    >
                                        <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                                    </ButtonBase>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Collapse>
        </Box>
    );
};

const DetailsLinkedItems = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === "RELATION";

    const [addOpen, setAddOpen] = useState(false);
    const [removeLinkedItem] = useRemoveSupportTicketLinkedItemMutation();

    const [orderId, setOrderId] = useState(null);
    const [orderExtended, setOrderExtended] = useState(false);
    const [orderLoad, setOrderLoad] = useState(false);

    const [invoiceId, setInvoiceId] = useState(null);
    const [invoiceExtended, setInvoiceExtended] = useState(false);
    const [invoiceLoad, setInvoiceLoad] = useState(false);

    const [productId, setProductId] = useState(null);
    const [productExtended, setProductExtended] = useState(false);
    const [productLoad, setProductLoad] = useState(false);

    const [inboundId, setInboundId] = useState(null);
    const [inboundExtended, setInboundExtended] = useState(false);
    const [inboundLoad, setInboundLoad] = useState(false);

    const [returnId, setReturnId] = useState(null);
    const [returnExtended, setReturnExtended] = useState(false);
    const [returnLoad, setReturnLoad] = useState(false);

    const [webshopId, setWebshopId] = useState(null);
    const [webshopExtended, setWebshopExtended] = useState(false);
    const [webshopLoad, setWebshopLoad] = useState(false);

    const [linkedTicketId, setLinkedTicketId] = useState(null);
    const [linkedTicketOpen, setLinkedTicketOpen] = useState(false);

    const linkedItems = props.ticket?.linkedItems ?? [];

    const openLinked = (linked) => {
        switch (linked.linkedType) {
            case "order":
                setOrderId(linked.linkedId);
                if (!orderLoad) setOrderLoad(true);
                setOrderExtended(true);
                break;
            case "invoice":
                setInvoiceId(linked.linkedId);
                if (!invoiceLoad) setInvoiceLoad(true);
                setInvoiceExtended(true);
                break;
            case "product":
                setProductId(linked.linkedId);
                if (!productLoad) setProductLoad(true);
                setProductExtended(true);
                break;
            case "inbound":
                setInboundId(linked.linkedId);
                if (!inboundLoad) setInboundLoad(true);
                setInboundExtended(true);
                break;
            case "return":
                setReturnId(linked.linkedId);
                if (!returnLoad) setReturnLoad(true);
                setReturnExtended(true);
                break;
            case "webshopcredentials":
                setWebshopId(linked.linkedId);
                if (!webshopLoad) setWebshopLoad(true);
                setWebshopExtended(true);
                break;
            case "ticket":
                setLinkedTicketId(linked.linkedId);
                setLinkedTicketOpen(true);
                break;
            default:
                return;
        }
    };

    const handleRemove = async (e, itemId) => {
        e.stopPropagation();
        if (!props.ticket?.id) return;
        try {
            const result = await removeLinkedItem({
                id: props.ticket.id,
                itemId,
            }).unwrap();
            showToastMessageUpdateRequest(t, result);
        } catch (err) {
            showToastMessageRequestError(t("delete_failed"), t, err);
        }
    };

    return (
        <Box sx={{ mt: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Subheading semibold>{t("linked_items")}</Subheading>
                {!props.locked && (
                    <ButtonBase
                        onClick={() => setAddOpen(true)}
                        sx={{
                            width: 24,
                            height: 24,
                            borderRadius: 3,
                            background:
                                theme.palette.mode === "dark"
                                    ? colors.grey[400]
                                    : colors.grey[100],
                            color: colors.txt["primary"],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <AddIcon sx={{ fontSize: 16 }} />
                    </ButtonBase>
                )}
            </Box>
            {linkedItems?.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                    {linkedItems.map((linked) => {
                        const bg = generateColorFromTitle(linked.linkedType, theme.palette.mode, 90);
                        const fg = generateIconColorFromTitle(linked.linkedType, theme.palette.mode);
                        const searchKey = support_ticket_linked_type_to_search_key[linked.linkedType];
                        const icon =
                            linked.linkedType === "webshopcredentials"
                                ? <StoreOutlinedIcon sx={{ fontSize: 14 }} />
                                : search_keys[searchKey]?.icon;

                        return (
                            <ButtonBase
                                key={"li" + linked.id}
                                onClick={() => openLinked(linked)}
                                TouchRippleProps={{
                                    style: {
                                        color:
                                            theme.palette.mode === "dark"
                                                ? colors.grey[600]
                                                : colors.grey[300],
                                    },
                                }}
                                sx={{
                                    borderRadius: 7,
                                    width: 1,
                                    py: 0.5,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-start",
                                    gap: 1.5,
                                    "& .li-remove": {
                                        opacity: 0,
                                        visibility: "hidden",
                                        transition: "150ms",
                                    },
                                    "&:hover": {
                                        "& > *:first-of-type": {
                                            flex: "0 1 auto",
                                            maxWidth: "100%",
                                        },
                                        "& .li-remove": {
                                            opacity: 1,
                                            visibility: "visible",
                                        },
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        height: 32,
                                        flex: "0 0 auto",
                                        px: 1.5,
                                        pl: 0.5,
                                        borderRadius: 4,
                                        maxWidth: 150,
                                        minWidth: 150,
                                        transition: "flex 250ms, max-width 250ms",
                                        background: bg,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            borderRadius: 4,
                                            height: 24,
                                            width: 24,
                                            flexShrink: 0,
                                            background: fg,
                                            color: theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light'],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            "& svg": { fontSize: 14 },
                                            "& svg path": { fill: theme.palette.mode === "dark" ? colors.txt['primaryDark'] : colors.txt['light'] },
                                        }}
                                    >
                                        {icon}
                                    </Box>
                                    <Text semibold sx={{ display: "block" }} noBreak>
                                        {linked.displayReference || linked.linkedId}
                                    </Text>
                                </Box>
                                <Text sx={{ display: "block", flex: 1, textAlign: "left" }} noBreak>
                                    {linked.displayName}
                                </Text>
                                {!props.locked && (
                                    <Box
                                        className="li-remove"
                                        component="span"
                                        onClick={(e) => handleRemove(e, linked.id)}
                                        sx={{
                                            ml: "auto",
                                            width: 24,
                                            height: 24,
                                            borderRadius: 3,
                                            flexShrink: 0,
                                            background:colors.red[100],
                                            color: colors.red[400],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            "&:hover" : {
                                                background:colors.red[200],
                                            }
                                        }}
                                    >
                                        <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
                                    </Box>
                                )}
                            </ButtonBase>
                        );
                    })}
                </Box>
            )}

            <MAddSupportTicketLinkedItem
                open={addOpen}
                handleClose={() => setAddOpen(false)}
                ticketId={props.ticket?.id}
                relationId={props.ticket?.relationId ?? props.ticket?.relation?.id}
                linkedItems={linkedItems}
            />

            {orderLoad && (
                <SpOrderDetails
                    isExtended={orderExtended}
                    setIsExtended={setOrderExtended}
                    orderId={orderId}
                />
            )}
            {invoiceLoad && (
                <SpInvoiceDetails
                    isExtended={invoiceExtended}
                    setIsExtended={setInvoiceExtended}
                    invoiceId={invoiceId}
                />
            )}
            {productLoad && (
                <SPStockDetails
                    isExtended={productExtended}
                    setIsExtended={setProductExtended}
                    productId={productId}
                />
            )}
            {inboundLoad && (
                <SpInboundDetails
                    isExtended={inboundExtended}
                    setIsExtended={setInboundExtended}
                    inboundId={inboundId}
                />
            )}
            {returnLoad && (
                <SpReturnDetails
                    isExtended={returnExtended}
                    setIsExtended={setReturnExtended}
                    returnId={returnId}
                />
            )}
            {webshopLoad && (
                <SpWebshopCredentialsDetails
                    isExtended={webshopExtended}
                    setIsExtended={setWebshopExtended}
                    webshopCredentialsId={webshopId}
                />
            )}
            {linkedTicketOpen && (
                <MTicketDetailBottom
                    open={linkedTicketOpen}
                    onClose={() => setLinkedTicketOpen(false)}
                    ticketId={linkedTicketId}
                    readOnly
                    viewer={isRelation ? 'client' : 'fulfilmentcenter'}
                />
            )}
        </Box>
    );
};

const AssigneeUserOption = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <ButtonBase
            onClick={props.onClick}
            sx={{
                flexShrink: 0,
                height: 32,
                width: 1,
                pl: 1.5,
                pr: 0.5,
                textAlign: "left",
                display: "flex",
                justifyContent: 'flex-start',
                alignItems: "center",
                borderRadius: 2,
                background: props.isSelected
                    ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                    : 'transparent',
                color: props.isSelected
                    ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                    : colors.txt["primary"],
                fontWeight: props.isSelected ? 700 : 400,
            }}
        >
            {props.option?.name}
        </ButtonBase>
    );
};

const DetailsParticipants = ({ messages, ticket, locked }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const isRelation = selectedEnvironmentType === "RELATION";
    const canAssign = !isRelation && !locked;

    const [updateTicket] = useUpdateSupportTicketMutation();

    const assignedToId = ticket?.assignedToId ?? ticket?.assignee?.id ?? null;

    const participants = useMemo(() => {
        // One row per person per side (message_type). A person can hold several
        // roles on the fulfilment side — e.g. they posted messages AND are the
        // assignee — so we accumulate roles per person and render them
        // comma-separated instead of as duplicate rows.
        const participantMap = new Map();

        // Find this person's existing entry within a side, collapsing the
        // uid-vs-name split: system / assignment messages carry no user id, so
        // the same person would otherwise key twice.
        const findEntry = (type, uid, name) => {
            for (const entry of participantMap.values()) {
                if (entry.type !== type) continue;
                if (uid != null && entry.userId === uid) return entry;
                if (entry.name === name) return entry;
            }
            return null;
        };

        const upsert = (type, uid, name, role) => {
            let entry = findEntry(type, uid, name);
            if (!entry) {
                entry = { userId: uid, name, type, roles: [] };
                participantMap.set(`${type}:${uid ?? name}`, entry);
            } else if (entry.userId == null && uid != null) {
                entry.userId = uid; // backfill the id from a richer message
            }
            if (role && !entry.roles.includes(role)) entry.roles.push(role);
        };

        messages.forEach(msg => {
            // Activity updates (assignment, status change, linking) don't make
            // their actor a conversation participant — only real messages do.
            // Without this, assigning a colleague lists the assigner too.
            if (msg.content?.type !== 'text' && msg.content?.type !== 'attachments') return;
            upsert(msg.message_type, msg.user?.id ?? null, msg.senderName, msg.user?.role || null);
        });

        // Surface / mark the current assignee (fulfilment side). On the relation
        // side `ticket.assignee` is `{ name }` only (no id); on the warehouse
        // side it carries the user id. Either way it folds into the person's
        // existing row when they've already posted.
        if (ticket?.assignee?.name) {
            upsert(MESSAGE_TYPES.FULFILMENTCENTER, ticket.assignee.id ?? null, ticket.assignee.name, 'assignee');
        }

        const participantList = Array.from(participantMap.values());

        participantList.sort((a, b) => {
            const orderA = MESSAGE_TYPE_ORDER.indexOf(a.type);
            const orderB = MESSAGE_TYPE_ORDER.indexOf(b.type);
            return orderA - orderB;
        });

        return participantList.filter(p =>
            p.type === MESSAGE_TYPES.FULFILMENTCENTER ||
            p.type === MESSAGE_TYPES.STOCKHUB ||
            p.type === MESSAGE_TYPES.CLIENT
        );
    }, [messages, ticket?.assignee?.id, ticket?.assignee?.name]);

    const handleAssign = async (selected, object) => {
        if (!ticket?.id) return;
        // ShDropdownQuery returns null/-1 on deselection (clicking the active user again).
        const nextId = selected === null || selected === -1 ? null : (object?.id ?? selected);
        if (nextId === assignedToId) return;
        try {
            const res = await updateTicket({
                id: ticket.id,
                data: { assignedToId: nextId },
            }).unwrap();
            showToastMessageUpdateRequest(t, res);
        } catch (e) {
            showToastMessageRequestError(t("update_failed"), t, e);
        }
    };

    const handleUnassign = async () => {
        if (!ticket?.id) return;
        try {
            const res = await updateTicket({
                id: ticket.id,
                data: { assignedToId: null },
            }).unwrap();
            showToastMessageUpdateRequest(t, res);
        } catch (e) {
            showToastMessageRequestError(t("update_failed"), t, e);
        }
    };

    const getAvatarStyle = (type) => {
        switch (type) {
            case MESSAGE_TYPES.FULFILMENTCENTER:
                return {
                    background: colors.orange[100],
                    color: colors.orange[400],
                };
            case MESSAGE_TYPES.STOCKHUB:
                return {
                    background: colors.primary[100],
                    color: colors.primary[400],
                };
            case MESSAGE_TYPES.CLIENT:
            default:
                return {
                    background: theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                    color: theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light'],
                };
        }
    };

    const getLabelVariant = (type) => {
        switch (type) {
            case MESSAGE_TYPES.FULFILMENTCENTER:
                return 'orange';
            case MESSAGE_TYPES.STOCKHUB:
                return theme.palette.mode === "dark" ? "blue" : "primary";
            case MESSAGE_TYPES.CLIENT:
            default:
                return theme.palette.mode === "dark" ? "greyLight" : 'grey';
        }
    };

    const getInitials = (name) => {
        if (!name) return '';
        const parts = name.split(' ').filter(Boolean);
        if (parts.length === 1) return parts[0][0].toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    return (
        <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Subheading semibold>{t("participants")}</Subheading>
                {canAssign && (
                    <ShDropdownQuery
                        name="name"
                        valueKey="id"
                        dataKey={null}
                        content={useGetUsersSelectQuery}
                        config={ASSIGNEE_FILTER}
                        selected={assignedToId ?? -1}
                        shComponent={<AssigneeUserOption />}
                        onChange={handleAssign}
                        closeOnSelection
                    >
                        {({ onClick }) => (
                            <ButtonBase
                                onClick={onClick}
                                sx={{
                                    height: 24,
                                    px: 1.25,
                                    borderRadius: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                    color: colors.txt['primary'],
                                }}
                            >
                                <AddIcon sx={{ fontSize: 14 }} />
                                <Text semibold sx={{ fontSize: 12 }}>{t("assign_to")}</Text>
                            </ButtonBase>
                        )}
                    </ShDropdownQuery>
                )}
            </Box>
            <Box sx={{ mt: 1 }}>
                {participants.map((participant, index) => {
                    const avatarStyle = getAvatarStyle(participant.type);
                    const initials = getInitials(participant.name);
                    const isAssignee = !isRelation
                        && participant.userId
                        && assignedToId
                        && participant.userId === assignedToId;

                    return (
                        <Box
                            key={index}
                            sx={{
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                // overflow:"hidden",
                            }}
                        >
                            {/* Avatar */}
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    display: 'flex',
                                    width: 32,
                                    height: 32,
                                    borderRadius: 4,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 14,
                                    fontWeight: 700,
                                    fontFamily: "Roboto, sans-serif",
                                    ...avatarStyle,
                                }}
                            >
                                {initials}
                            </Box>
                            {/* Name and role */}
                            <Box
                                sx={{
                                    flex: 1,
                                    display:'flex',
                                    flexDirection:'column',
                                    overflow:"hidden",
                                }}
                            >
                                <Text noBreak>
                                    {participant.name}
                                </Text>
                                <Text fontSize={12} light noBreak>{participant.roles?.length ? participant.roles.map((r) => t(r)).join(", ") : "admin"}</Text>
                            </Box>
                            
                            {!isRelation && isAssignee && (
                                <Box
                                    sx={{
                                        width:24,
                                        height:24,
                                        border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                        background: theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                        color: theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                        borderRadius:3,
                                        lineHeight:'24px',
                                        textAlign:"center"
                                    }}
                                >
                                    <HowToRegOutlinedIcon
                                        titleAccess={t("assignee")}
                                        sx={{
                                            fontSize:16,
                                        }}
                                    />
                                </Box>
                            )}

                            {canAssign && isAssignee && (
                                <ButtonBase
                                    onClick={handleUnassign}
                                    title={t("unassign")}
                                    sx={{
                                        width:24,
                                        height:24,
                                        borderRadius:3,
                                        flexShrink:0,
                                        background:colors.red[100],
                                        color:colors.red[400],
                                        display:'flex',
                                        alignItems:'center',
                                        justifyContent:'center',
                                        "&:hover": { background:colors.red[200] },
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize:14 }} />
                                </ButtonBase>
                            )}

                            <ShLabel
                                borderless
                                fitted
                                palette="normal"
                                size={24}
                                variant={getLabelVariant(participant.type)}
                                sx={{
                                    lineHeight: "24px",
                                    flexShrink: 0,
                                    fontWeight:"400",
                                }}
                            >
                                {t(participant.type)}
                            </ShLabel>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

const TicketStatusLabel = ({ status, theme }) => {
    const { t } = useTranslation();
    const { labelcolor, labeltext } = getSupportTicketStateData(status || support_ticket_status.OPEN, theme);
    return (
        <ShLabel
            fitted
            palette="normal"
            size={24}
            variant={labelcolor}
            sx={{ lineHeight: "24px" }}
        >
            {t(labeltext)}
        </ShLabel>
    );
};